/**
 * Live video wire types — `GET /stream/cameras` and `POST /stream/tokens`.
 *
 * ⚠️ This family is **camelCase on the wire**, unlike every `/control/*` route in this
 * app, which is snake_case. That is not a transcription slip here: the payloads in
 * `docs/api/cameras/live-video.md` §3.1–3.2 are camelCase, because this surface was written
 * against the ONVIF WebRTC spec rather than against sv_control's protos. Keeping the
 * types 1:1 with the wire (as every other entity here does) therefore means camelCase
 * in this one file, and it is worth the inconsistency: a hand-written remap is exactly
 * where a field silently becomes `undefined`.
 */

/** One encoding of one camera. `token` is what `connect` asks for, NOT the name. */
import type {CameraCapabilities, CameraSettings} from '@/entities/stream/settings';

export type StreamProfile = {
  token: string;
  name: string;
  /** `H264` or `H265`. A browser cannot play H265 — see `playableProfiles`. */
  codec: string;
  width: number;
  height: number;
  fps: number;
  bitrateKbps: number;
};

export type StreamCamera = {
  /** `{source}:{cameraId}` — the id the token is minted for. */
  deviceId: string;
  /**
   * The BOX, not the camera. An NVR has one peer for all 16 channels, and passing a
   * `deviceId` here is the most common mistake on this API: the answer is `480`,
   * indistinguishable from "camera is off" (cameras/live-video.md §1.3).
   */
  peer: string;
  name: string;
  deviceType: string;
  /**
   * False ⇒ the source stopped reporting this camera. The row is KEPT on purpose so
   * a camera that vanished is visible as missing rather than silently absent from the
   * list. Do not call into one.
   */
  available: boolean;
  /**
   * The camera's source holds a socket at the signalling server right now.
   * `available && !online` = switched off or cut off from the internet. Reads false
   * when the server could not ask, rather than failing the list.
   */
  online?: boolean;
  /**
   * The SOURCE's clock at the moment of the snapshot, not the server's. Fine for
   * "data as of …"; meaningless compared against Date.now().
   */
  snapshotAtMillis: number;
  profiles: StreamProfile[];
  /**
   * Поворотная камера: источник сообщает `ptz` в снимке профилей
   * (cameras/live-video.md §6.1), а в каталог гейтвей его пока не пробрасывает —
   * поле запрошено (CABINET-BACKEND-ASKS.md §6). Нет поля — нет пульта.
   */
  ptz?: boolean;
};

/** `GET /stream/cameras` — the gateway's usual envelope, list in `data`. */
export type StreamCameraList = {
  data: StreamCamera[];
};

/**
 * `POST /stream/tokens`. Short-lived (minutes) and single-purpose: the spec permits
 * passing it as a query parameter, query parameters end up in proxy logs, and the
 * cabinet's own session must never be the thing that lands there.
 */
export type StreamToken = {
  token: string;
  expiresInSeconds: number;
  peer: string;
  /**
   * The whitelist baked into the token's claims. `connect` with anything else is a
   * `403`, and `connect` with no profile at all is also a `403` — without the list,
   * a token minted after a permission check on `ch01` would reach `ch02` by changing
   * one field (cameras/live-video.md §3.2).
   */
  profiles: string[];
};

/**
 * The profiles a browser or a phone can actually render, in descending resolution.
 *
 * H265 is filtered rather than merely warned about: a `<video>` handed an H265 track
 * shows a black rectangle and no error, which reads as a broken camera. A camera with
 * no H264 profile is reported as such by the caller.
 */
export function playableProfiles(camera: StreamCamera): StreamProfile[] {
  return camera.profiles
    .filter(p => p.codec.toUpperCase() === 'H264')
    .slice()
    .sort((a, b) => b.width * b.height - a.width * a.height);
}

/**
 * The profile to open with: the SMALLEST playable one.
 *
 * A wall of thumbnails at 1080p/4Mbps is how a site's uplink is saturated by the act
 * of looking at it. The sub-stream is what the operator wants for "is anyone at the
 * gate"; the main stream is a deliberate choice, offered next to the picture.
 */
export function defaultProfile(camera: StreamCamera): StreamProfile | null {
  const playable = playableProfiles(camera);
  return playable.length > 0 ? playable[playable.length - 1] : null;
}

/** `applied` — the camera runs what the cabinet wants; `pending` — it has not picked
 *  the change up yet (offline, or between a change and its next pull); `failed` — it
 *  tried and refused, `applyError` says why. */
export type SourceConfigStatus = 'applied' | 'pending' | 'failed';

export type SourceConfig = {
  sourceId: string;
  desired: CameraSettings;
  desiredVersion: number;
  reported: CameraSettings;
  appliedVersion: number;
  capabilities: CameraCapabilities;
  applyError: string | null;
  status: SourceConfigStatus;
  reportedAt: string | null;
  updatedAt: string | null;
};

export type SourceCommandName = 'reboot' | 'sync_time' | 'refresh_profiles';

/**
 * Пульт PTZ — тем же каналом команд (`POST /stream/sources/{id}/commands`). Команды
 * ЗАПРОШЕНЫ, гейтвей их пока не знает и ответит `422` (CABINET-BACKEND-ASKS.md §6).
 * Смысл — ONVIF ContinuousMove / Stop / GotoHomePosition.
 */
export type PtzCommandName = 'ptz_move' | 'ptz_stop' | 'ptz_home';

export type SourceCommand = {
  id: string;
  command: SourceCommandName | PtzCommandName;
  params: Record<string, unknown>;
  status: 'queued' | 'delivered' | 'done' | 'failed' | 'expired';
  result: Record<string, unknown>;
  createdAt: string;
  finishedAt: string | null;
};

/** What an owner pastes into a camera added by hand. `secret` is shown once:
 *  only its hash is stored, it can be replaced but never shown again. */
export type CameraCredentials = {
  sourceId: string;
  secret: string;
  signalingUrl: string;
};
